import { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import {
  TrendingUp,
  ArrowRight,
  BarChart3,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface HeroDashboardProps {
  portfolioValue: number;
  currentAge: number;
  retirementAge: number;
  goalAmount: number;
  expectedReturn?: number;
  volatility?: number;
  crisisImpact?: number;
  tacticalProtectionFactor?: number;
}

interface SimulationResult {
  successRate: number;
  medianOutcome: number;
  worstCase: number;
  bestCase: number;
}

// Run a quick Monte Carlo simulation
function runSimulation(
  portfolioValue: number,
  years: number,
  expectedReturn: number,
  volatility: number,
  goalAmount: number,
  annualContribution: number = 20000,
  numSims: number = 2000
): SimulationResult {
  const outcomes: number[] = [];
  let successCount = 0;

  const randomNormal = () => {
    let u = 0, v = 0;
    while (u === 0) u = Math.random();
    while (v === 0) v = Math.random();
    return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
  };

  for (let sim = 0; sim < numSims; sim++) {
    let value = portfolioValue;
    for (let year = 0; year < years; year++) {
      const annualReturn = expectedReturn + volatility * randomNormal();
      value = value * (1 + annualReturn) + annualContribution;
    }
    outcomes.push(value);
    if (value >= goalAmount) successCount++;
  }

  outcomes.sort((a, b) => a - b);

  const getPercentile = (p: number) => outcomes[Math.floor(outcomes.length * p / 100)];

  return {
    successRate: (successCount / numSims) * 100,
    medianOutcome: getPercentile(50),
    worstCase: getPercentile(10),
    bestCase: getPercentile(90),
  };
}

export function HeroDashboard({
  portfolioValue,
  currentAge,
  retirementAge,
  goalAmount,
  expectedReturn = 0.07,
  volatility = 0.15,
  crisisImpact = 0.35,
  tacticalProtectionFactor = 0.6,
}: HeroDashboardProps) {
  const years = Math.max(retirementAge - currentAge, 1);

  // Calculate current plan projection
  const currentPlan = useMemo(() => {
    return runSimulation(portfolioValue, years, expectedReturn, volatility, goalAmount);
  }, [portfolioValue, years, expectedReturn, volatility, goalAmount]);

  // Calculate optimized plan projection (lower volatility, better protection)
  const optimizedPlan = useMemo(() => {
    // Optimized reduces volatility significantly and slightly adjusts return
    const optimizedVolatility = volatility * 0.70;
    const optimizedReturn = expectedReturn * 0.97;
    return runSimulation(portfolioValue, years, optimizedReturn, optimizedVolatility, goalAmount);
  }, [portfolioValue, years, expectedReturn, volatility, goalAmount]);

  // Calculate stress-tested values (crisis losses)
  const crisisAnalysis = useMemo(() => {
    // Expected loss in a crisis scenario
    const currentLoss = currentPlan.medianOutcome * crisisImpact;
    const optimizedLoss = optimizedPlan.medianOutcome * crisisImpact * tacticalProtectionFactor;

    return {
      currentLoss,
      optimizedLoss,
      savedAmount: currentLoss - optimizedLoss,
    };
  }, [currentPlan, optimizedPlan, crisisImpact, tacticalProtectionFactor]);

  // Gap analysis
  const gap = optimizedPlan.medianOutcome - currentPlan.medianOutcome;
  const successGap = optimizedPlan.successRate - currentPlan.successRate;

  // Format helpers
  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  // Generate trajectory data for chart - FULL RANGE from current age to retirement
  const trajectoryData = useMemo(() => {
    const data = [];
    const yearStep = years > 20 ? 2 : 1;

    for (let year = 0; year <= years; year += yearStep) {
      const age = currentAge + year;

      // Compound growth with contributions
      const currentValue = portfolioValue * Math.pow(1 + expectedReturn, year) + 20000 * year;
      const optimizedValue = portfolioValue * Math.pow(1 + expectedReturn * 0.97, year) + 20000 * year;

      data.push({
        age,
        year,
        current: currentValue,
        optimized: optimizedValue,
        goal: goalAmount,
      });
    }

    // Ensure we always include the final retirement age
    if (data[data.length - 1]?.age !== retirementAge) {
      const currentValue = portfolioValue * Math.pow(1 + expectedReturn, years) + 20000 * years;
      const optimizedValue = portfolioValue * Math.pow(1 + expectedReturn * 0.97, years) + 20000 * years;
      data.push({
        age: retirementAge,
        year: years,
        current: currentValue,
        optimized: optimizedValue,
        goal: goalAmount,
      });
    }

    return data;
  }, [portfolioValue, years, currentAge, retirementAge, expectedReturn, goalAmount]);

  // Success probability color
  const getSuccessColor = (rate: number) => {
    if (rate >= 80) return 'text-green-500';
    if (rate >= 60) return 'text-amber-500';
    return 'text-red-500';
  };

  const getSuccessBg = (rate: number) => {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 60) return 'bg-amber-500';
    return 'bg-red-500';
  };

  // Calculate tick interval for X-axis
  const xAxisInterval = useMemo(() => {
    if (years <= 5) return 0;
    if (years <= 15) return 2;
    if (years <= 30) return 4;
    return 5;
  }, [years]);

  return (
    <Card className="border-border/50 bg-gradient-to-br from-card to-muted/20">
      <CardContent className="p-4 sm:p-6">
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">

          {/* Left Column: Success Probability + Downturn Analysis */}
          <div className="lg:col-span-4 space-y-4">
            {/* Success Rate */}
            <div className="p-4 rounded-xl bg-background/80 border border-border/50 backdrop-blur">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
                  Goal Success Probability
                </span>
                <Badge variant="outline" className="text-[10px]">
                  Monte Carlo
                </Badge>
              </div>

              <div className="flex items-end gap-3 mb-3">
                <span className={cn('text-5xl font-bold font-mono', getSuccessColor(currentPlan.successRate))}>
                  {currentPlan.successRate.toFixed(0)}%
                </span>
                {successGap > 2 && (
                  <div className="flex items-center gap-1 text-green-500 text-sm mb-2">
                    <TrendingUp className="h-4 w-4" />
                    <span>+{successGap.toFixed(0)}% optimized</span>
                  </div>
                )}
              </div>

              <Progress
                value={currentPlan.successRate}
                className={cn('h-2', getSuccessBg(currentPlan.successRate))}
              />

              <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
                <span>0%</span>
                <span>Goal: {formatCurrency(goalAmount)}</span>
                <span>100%</span>
              </div>
            </div>

            {/* Market Downturn Analysis */}
            <div className="p-4 rounded-xl bg-background/80 border border-border/50">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">
                Market Downturn Exposure
              </div>

              <div className="space-y-3 text-sm">
                <p className="text-muted-foreground leading-relaxed">
                  As currently configured, your portfolio is expected to lose{' '}
                  <span className="text-red-500 font-semibold font-mono">
                    {formatCurrency(crisisAnalysis.currentLoss)}
                  </span>{' '}
                  in the event of a COVID-like market downturn.
                </p>

                <p className="text-muted-foreground leading-relaxed">
                  With optimized protections in place, we estimate your expected loss can be reduced to{' '}
                  <span className="text-amber-500 font-semibold font-mono">
                    {formatCurrency(crisisAnalysis.optimizedLoss)}
                  </span>.
                </p>

                <div className="pt-2 border-t border-border/50 flex items-center justify-between">
                  <span className="text-muted-foreground">Potential savings</span>
                  <span className="text-green-500 font-mono font-semibold">
                    {formatCurrency(crisisAnalysis.savedAmount)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Trajectory Chart + Projected Values */}
          <div className="lg:col-span-8">
            <div className="p-4 rounded-xl bg-background/80 border border-border/50 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold">Wealth Trajectory</h3>
                  <p className="text-xs text-muted-foreground">
                    Projected portfolio growth from age {currentAge} to {retirementAge}
                  </p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-0.5 bg-muted-foreground/50" />
                    <span>Current</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-0.5 bg-primary" />
                    <span>Optimized</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-0.5 bg-green-500 border-dashed" style={{ borderTop: '2px dashed' }} />
                    <span>Goal</span>
                  </div>
                </div>
              </div>

              <div className="h-[220px] flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trajectoryData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                    <defs>
                      <linearGradient id="currentGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6b7280" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#6b7280" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="optimizedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.5}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="age"
                      tick={{ fontSize: 11 }}
                      tickFormatter={(age) => `${age}`}
                      interval={xAxisInterval}
                      axisLine={{ stroke: '#374151' }}
                      tickLine={{ stroke: '#374151' }}
                    />
                    <YAxis
                      tick={{ fontSize: 11 }}
                      tickFormatter={(val) => formatCurrency(val)}
                      width={55}
                      axisLine={{ stroke: '#374151' }}
                      tickLine={{ stroke: '#374151' }}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        return (
                          <div className="bg-background/95 border border-border rounded-lg p-3 shadow-lg text-xs">
                            <div className="font-semibold mb-2">Age {label}</div>
                            <div className="space-y-1.5">
                              <div className="flex justify-between gap-4">
                                <span className="text-muted-foreground">Current:</span>
                                <span className="font-mono font-medium">{formatCurrency(payload[0]?.value as number)}</span>
                              </div>
                              <div className="flex justify-between gap-4">
                                <span className="text-primary">Optimized:</span>
                                <span className="font-mono font-medium">{formatCurrency(payload[1]?.value as number)}</span>
                              </div>
                              <div className="flex justify-between gap-4 pt-1 border-t border-border/50">
                                <span className="text-green-500">Goal:</span>
                                <span className="font-mono font-medium">{formatCurrency(goalAmount)}</span>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    />
                    <ReferenceLine
                      y={goalAmount}
                      stroke="#22c55e"
                      strokeDasharray="5 5"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="current"
                      stroke="#6b7280"
                      fill="url(#currentGradient)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="optimized"
                      stroke="#8b5cf6"
                      fill="url(#optimizedGradient)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Projected Values at Retirement - Below Chart */}
              <div className="mt-4 pt-4 border-t border-border/50">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-3">
                  Projected Value at Age {retirementAge}
                </div>
                <div className="grid grid-cols-3 gap-6">
                  {/* Current Plan */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full bg-muted-foreground/50" />
                      <span className="text-sm text-muted-foreground">Current Plan</span>
                    </div>
                    <span className="text-2xl font-bold font-mono">
                      {formatCurrency(currentPlan.medianOutcome)}
                    </span>
                  </div>

                  {/* Optimized Plan */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-3 h-3 rounded-full bg-primary" />
                      <span className="text-sm text-muted-foreground">Optimized Plan</span>
                    </div>
                    <span className="text-2xl font-bold font-mono text-primary">
                      {formatCurrency(optimizedPlan.medianOutcome)}
                    </span>
                  </div>

                  {/* Opportunity Gap */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-muted-foreground">Opportunity Gap</span>
                    </div>
                    <span className={cn(
                      'text-2xl font-bold font-mono',
                      gap > 0 ? 'text-green-500' : 'text-muted-foreground'
                    )}>
                      {gap > 0 ? '+' : ''}{formatCurrency(gap)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Row */}
        <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <BarChart3 className="h-4 w-4" />
            <span>Based on {years}-year projection with 2,000 Monte Carlo simulations</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-primary font-medium cursor-pointer hover:underline">
            <span>See detailed analysis</span>
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
